import { useState, FormEvent, useEffect } from "react";
import { Modal } from "../ui/Modal";
import { Spinner } from "../ui/Spinner";
import { api, mensajeError } from "../../api/client";

export const MOTIVOS_BAJA_MASCOTA = [
  { value: "FALLECIMIENTO", label: "Falleció" },
  { value: "PETICION_CLIENTE", label: "Petición del cliente" },
  { value: "YA_NO_ES_CLIENTE", label: "Ya no es cliente del negocio" },
  { value: "OTRO", label: "Otro motivo" },
];

interface MascotaBaja {
  id: string;
  nombre: string;
}

interface Props {
  mascota: MascotaBaja | null;
  onClose: () => void;
  onBajaExitosa: () => void;
}

// Modal reutilizable para "dar de baja" (desvincular) una mascota del
// negocio: pide un motivo obligatorio antes de confirmar, y deja muy claro
// que no se borra ningún dato — solo deja de aparecer en el panel del
// negocio. Se usa tanto desde la lista de Mascotas como desde la ficha de
// MascotaDetalle.
export function ModalBajaMascota({ mascota, onClose, onBajaExitosa }: Props) {
  const [motivo, setMotivo] = useState("FALLECIMIENTO");
  const [detalle, setDetalle] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mascota) {
      setMotivo("FALLECIMIENTO");
      setDetalle("");
      setError(null);
    }
  }, [mascota]);

  async function confirmar(e: FormEvent) {
    e.preventDefault();
    if (!mascota) return;
    setGuardando(true);
    setError(null);
    try {
      await api.post(`/mascotas/${mascota.id}/eliminar-del-negocio`, {
        motivo,
        motivoDetalle: motivo === "OTRO" ? detalle.trim() : undefined,
      });
      onBajaExitosa();
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal open={!!mascota} onClose={onClose} title={`Dar de baja a ${mascota?.nombre ?? ""}`}>
      <form onSubmit={confirmar} className="space-y-4">
        <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-500">
          Esta mascota dejará de aparecer en tu panel, pero su expediente <strong>no se borra</strong>: el cliente lo sigue
          viendo en su portal (con el aviso de que ya no tiene veterinaria registrada), y sigue disponible para soporte
          de Mascotas Vet.
        </p>
        <div>
          <label className="label">Motivo de la baja</label>
          <select className="input" value={motivo} onChange={(e) => setMotivo(e.target.value)} required>
            {MOTIVOS_BAJA_MASCOTA.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
        {motivo === "OTRO" && (
          <div>
            <label className="label">Cuéntanos brevemente</label>
            <textarea
              className="input"
              rows={2}
              required
              value={detalle}
              onChange={(e) => setDetalle(e.target.value)}
              placeholder="Ej. se mudó de ciudad"
            />
          </div>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button
            type="submit"
            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
            disabled={guardando}
          >
            {guardando && <Spinner className="h-4 w-4 text-white" />}
            Confirmar baja
          </button>
        </div>
      </form>
    </Modal>
  );
}
